import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Form, Input, message, Row, Select, Table } from "antd";
import {
  DAILY_PACKING_ASSIGNMENT_EDIT_ENDPOINT,
  DAILY_PACKING_ASSIGNMENT_LINE_TABLE_ENDPOINT,
  DAILY_PACKING_ASSIGNMENT_POS_ENDPOINT,
  DAILY_PACKING_ASSIGNMENT_SPECIFIC_MAPPING_ENDPOINT,
  DAILY_PACKING_ASSIGNMENT_STORE_ENDPOINT,
  DAILY_PACKING_ASSIGNMENT_UPDATE_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/** Display label for packing table; never "-" or empty when table id exists. */
function packingTableOptionLabel(name, tableId) {
  const n = typeof name === "string" ? name.trim() : "";
  if (n && n !== "-" && n !== "—") return n;
  if (tableId != null && tableId !== "") {
    const num = Number(tableId);
    if (!Number.isNaN(num)) return `Packing table ${num}`;
  }
  return "";
}

const PackingAssignmentForm = forwardRef((props, ref) => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [poOptions, setPoOptions] = useState([]);
  const [lineMappings, setLineMappings] = useState([]);
  const [rows, setRows] = useState([]);
  const [deletedRows, setDeletedRows] = useState([]);
  const [selectedPo, setSelectedPo] = useState(null);
  const [selectedPackingLineId, setSelectedPackingLineId] = useState(null);
  const [linePackingTables, setLinePackingTables] = useState([]);
  const poSearchTimer = useRef(null);

  const fetchPos = useCallback(async (search = "") => {
    const response = await getData(DAILY_PACKING_ASSIGNMENT_POS_ENDPOINT, false, {
      search,
      page: 1,
      per_page: 10,
    });
    if (response?.status === 200 || response?.data?.code === 200) {
      setPoOptions(response?.data?.data?.data || []);
    }
  }, []);

  const fetchLineAndTable = useCallback(async () => {
    const response = await getData(DAILY_PACKING_ASSIGNMENT_LINE_TABLE_ENDPOINT, false, { page: 1, per_page: 200 });
    if (response?.status === 200 || response?.data?.code === 200) {
      setLineMappings(response?.data?.data?.data || []);
    }
  }, []);

  useEffect(() => {
    fetchPos();
    fetchLineAndTable();
  }, [fetchPos, fetchLineAndTable]);
  const buildRowsFromLine = useCallback(async (packingLineId) => {
    if (!packingLineId) {
      setRows([]);
      setLinePackingTables([]);
      return;
    }

    if (isEdit && selectedPackingLineId && selectedPackingLineId !== packingLineId) {
      const previousExistingRows = rows.filter((row) => row?.id > 0);
      if (previousExistingRows.length) {
        setDeletedRows((prev) => {
          const existingIds = new Set(prev.map((item) => item?.id));
          const merged = [...prev];
          previousExistingRows.forEach((item) => {
            if (!existingIds.has(item.id)) {
              merged.push({ ...item, is_delete: 1 });
            }
          });
          return merged;
        });
      }
    } else if (!isEdit) {
      setDeletedRows([]);
    }

    setSelectedPackingLineId(packingLineId);
    setLoading(true);

    // API expects PackingOperatorMapping primary id, not packing_line_id
    const mapping = lineMappings.find(
      (item) => String(item?.packing_line_id) === String(packingLineId),
    );
    const mappingPrimaryId = mapping?.id;
    if (!mappingPrimaryId) {
      setRows([]);
      setLinePackingTables([]);
      setLoading(false);
      message.error("Packing line mapping not found");
      return;
    }

    const response = await getData(
      `${DAILY_PACKING_ASSIGNMENT_SPECIFIC_MAPPING_ENDPOINT}/${mappingPrimaryId}`,
    );
    const mapData = response?.data?.data;
    const operators = mapData?.operators || [];

    const uniqueTables = [
      ...new Map(
        operators
          .filter((item) => item?.packing_table_id != null)
          .map((item) => [
            String(item.packing_table_id),
            {
              id: Number(item.packing_table_id),
              name: packingTableOptionLabel(item?.packing_table?.name, item?.packing_table_id),
            },
          ]),
      ).values(),
    ];
    setLinePackingTables(uniqueTables);

    // ✅ FIX: Create one row per operator (each has its own table), not just the first one
    setRows(
      operators.map((item) => ({
        key: `line-${packingLineId}-op-${item?.operator_id}-${Date.now()}`,
        id: 0,
        operator_id: item?.operator_id || null,
        operator_name: item?.operator?.name || "N/A",
        packing_table_id: item?.packing_table_id || null,
        packing_table_name: packingTableOptionLabel(
          item?.packing_table?.name,
          item?.packing_table_id,
        ),
        is_delete: 0,
      })),
    );

    setLoading(false);
  }, [isEdit, rows, selectedPackingLineId, lineMappings]);
  // const buildRowsFromLine = useCallback(async (packingLineId) => {
  //   if (!packingLineId) {
  //     setRows([]);
  //     setLinePackingTables([]);
  //     return;
  //   }

  //   // In edit mode, if user changes line, mark previous existing details as deleted.
  //   if (isEdit && selectedPackingLineId && selectedPackingLineId !== packingLineId) {
  //     const previousExistingRows = rows.filter((row) => row?.id > 0);
  //     if (previousExistingRows.length) {
  //       setDeletedRows((prev) => {
  //         const existingIds = new Set(prev.map((item) => item?.id));
  //         const merged = [...prev];
  //         previousExistingRows.forEach((item) => {
  //           if (!existingIds.has(item.id)) {
  //             merged.push({ ...item, is_delete: 1 });
  //           }
  //         });
  //         return merged;
  //       });
  //     }
  //   } else if (!isEdit) {
  //     setDeletedRows([]);
  //   }

  //   setSelectedPackingLineId(packingLineId);
  //   setLoading(true);
  //   const response = await getData(`${DAILY_PACKING_ASSIGNMENT_SPECIFIC_MAPPING_ENDPOINT}/${packingLineId}`);
  //   const mapData = response?.data?.data;
  //   const operators = mapData?.operators || [];
  //   const uniqueTables = [
  //     ...new Map(
  //       operators
  //         .filter((item) => item?.packing_table_id != null)
  //         .map((item) => [
  //           String(item.packing_table_id),
  //           {
  //             id: Number(item.packing_table_id),
  //             name: packingTableOptionLabel(item?.packing_table?.name, item?.packing_table_id),
  //           },
  //         ]),
  //     ).values(),
  //   ];
  //   setLinePackingTables(uniqueTables);

  //   const first = operators?.[0];
  //   const firstTable = uniqueTables?.[0];
  //   setRows([
  //     {
  //       key: `line-${packingLineId}-${Date.now()}`,
  //       id: 0,
  //       operator_id: first?.operator_id || null,
  //       operator_name: first?.operator?.name || "N/A",
  //       packing_table_id: first?.packing_table_id || firstTable?.id || null,
  //       packing_table_name: packingTableOptionLabel(first?.packing_table?.name, first?.packing_table_id || firstTable?.id),
  //       is_delete: 0,
  //     },
  //   ]);
  //   setLoading(false);
  // }, [isEdit, rows, selectedPackingLineId]);

  const availableTablesForLine = lineMappings.filter(
    (item) => String(item?.packing_line_id) === String(selectedPackingLineId),
  );
  const packingTableOptions = [
    ...new Map(
      [
        ...linePackingTables.map((item) => ({
          value: String(item?.id),
          label: packingTableOptionLabel(item?.name, item?.id),
        })),
        ...availableTablesForLine
          .filter((item) => item?.packing_table_id != null && String(item.packing_table_id).trim() !== "")
          .map((item) => ({
            value: String(item.packing_table_id),
            label: packingTableOptionLabel(item?.packing_table?.name, item.packing_table_id),
          })),
        ...rows
          .filter((row) => row?.packing_table_id != null && String(row.packing_table_id).trim() !== "")
          .map((row) => ({
            value: String(row.packing_table_id),
            label: packingTableOptionLabel(row?.packing_table_name, row.packing_table_id),
          })),
      ]
        .filter((item) => item.value && item.label && item.value !== "undefined" && item.value !== "null")
        .map((item) => [item.value, item]),
    ).values(),
  ];

  const setPoDetailsToForm = (poRecord) => {
    setSelectedPo(poRecord || null);
    form.setFieldsValue({
      sales_contract_name: poRecord?.sales_contract?.reference_no || "",
      buyer_name: poRecord?.buyer?.name || "",
      buying_office_or_factory_name: poRecord?.buying_office?.name || poRecord?.factory_info?.name || "",
    });
  };

  useEffect(() => {
    if (!isEdit || !id) return;
    const loadEditData = async () => {
      setLoading(true);
      const response = await getData(`${DAILY_PACKING_ASSIGNMENT_EDIT_ENDPOINT}/${id}`);
      if (response?.status === 200 || response?.data?.code === 200) {
        const data = response?.data?.data;
        const poRecord = {
          po_map_id: data?.po_map_id,
          sales_contract_id: data?.sales_contract_id,
          buying_office_id: data?.buying_office_id,
          buyer_id: data?.buyer_id,
          sub_contract_factory_id: data?.sub_contract_factory_id,
          po_maps: data?.po_map,
          sales_contract: data?.sales_contract,
          buyer: data?.buyer,
          buying_office: data?.buying_office,
          factory_info: data?.sub_contract_factory,
        };

        setPoOptions((prev) => {
          const exists = prev.some((item) => item?.po_map_id === poRecord?.po_map_id);
          return exists ? prev : [poRecord, ...prev];
        });
        setPoDetailsToForm(poRecord);

        form.setFieldsValue({
          plan_date: data?.plan_date ? moment(data?.plan_date, "YYYY-MM-DD") : moment(),
          po_map_id: data?.po_map_id,
          packing_line_id: data?.packing_line_id,
        });
        setSelectedPackingLineId(data?.packing_line_id || null);

        setRows(
          (data?.details || []).map((item, idx) => ({
            key: `existing-${item?.id || idx}`,
            id: item?.id || 0,
            operator_id: item?.operator_id,
            operator_name: item?.operator?.name || "N/A",
            packing_table_id: item?.packing_table_id,
            packing_table_name: packingTableOptionLabel(item?.packing_table?.name, item?.packing_table_id),
            is_delete: 0,
          })),
        );
        setLinePackingTables(
          [
            ...new Map(
              (data?.details || [])
                .filter((item) => item?.packing_table_id != null)
                .map((item) => [
                  String(item.packing_table_id),
                  {
                    id: Number(item.packing_table_id),
                    name: packingTableOptionLabel(item?.packing_table?.name, item?.packing_table_id),
                  },
                ]),
            ).values(),
          ],
        );
      } else {
        message.error("Error loading packing assignment");
      }
      setLoading(false);
    };

    loadEditData();
  }, [isEdit, id, form]);

  const onFinish = async (values) => {
    if (!selectedPo?.po_map_id) {
      message.error("Please select PO");
      return;
    }

    const invalidRow = rows.find((row) => !row?.packing_table_id);
    if (invalidRow) {
      message.error("Please select packing table for all rows");
      return;
    }

    const details = rows.map((row) => ({
      id: row?.id || 0,
      operator_id: row?.operator_id,
      packing_table_id: row?.packing_table_id,
      is_delete: 0,
    }));

    if (isEdit) {
      deletedRows.forEach((row) => {
        details.push({
          id: row?.id || 0,
          operator_id: row?.operator_id,
          packing_table_id: row?.packing_table_id,
          is_delete: 1,
        });
      });
    }

    const bodyData = {
      plan_date: values?.plan_date ? moment(values.plan_date).format("DD-MM-YYYY") : null,
      packing_line_id: values.packing_line_id,
      sales_contract_id: selectedPo?.sales_contract_id || null,
      buying_office_id: selectedPo?.buying_office_id || null,
      buyer_id: selectedPo?.buyer_id || null,
      sub_contract_factory_id: selectedPo?.sub_contract_factory_id || null,
      po_map_id: selectedPo?.po_map_id,
      details,
    };

    setLoading(true);
    const endpoint = isEdit
      ? `${DAILY_PACKING_ASSIGNMENT_UPDATE_ENDPOINT}/${id}`
      : DAILY_PACKING_ASSIGNMENT_STORE_ENDPOINT;
    const response = await postData(endpoint, bodyData);
    if (response?.code === 200 || response?.status === 200) {
      message.success(isEdit ? "Packing Assignment Updated Successfully" : "Packing Assignment Created Successfully");
      navigate("/digitalization/packing/assignment");
    } else {
      message.error(response?.message || "Something went wrong");
    }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
  }));

  const addRow = () => {
    if (!selectedPackingLineId) {
      message.warning("Please select packing line first");
      return;
    }
    const selectedIds = new Set(rows.filter((row) => row?.packing_table_id).map((row) => String(row.packing_table_id)));
    const nextOption = packingTableOptions.find((item) => !selectedIds.has(String(item.value)));
    if (!nextOption) {
      message.warning("No more packing table available to add");
      return;
    }
    const lastRow = rows[rows.length - 1];
    setRows((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        id: 0,
        operator_id: lastRow?.operator_id || null,
        operator_name: lastRow?.operator_name || "N/A",
        packing_table_id: Number(nextOption?.value),
        packing_table_name: nextOption?.label,
        is_delete: 0,
      },
    ]);
  };

  const deleteRow = (key) => {
    setRows((prev) => {
      const target = prev.find((row) => row.key === key);
      if (!target) return prev;
      if (isEdit && target?.id > 0) {
        setDeletedRows((deletedPrev) => {
          if (deletedPrev.some((row) => row.id === target.id)) return deletedPrev;
          return [...deletedPrev, { ...target, is_delete: 1 }];
        });
      }
      return prev.filter((row) => row.key !== key);
    });
  };

  const updateRowField = (key, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        if (field === "packing_table_id") {
          const selectedTable = packingTableOptions.find((table) => String(table?.value) === String(value));
          return {
            ...row,
            packing_table_id: value ? Number(value) : null,
            packing_table_name: packingTableOptionLabel(
              selectedTable?.label ?? row?.packing_table_name,
              value ?? row?.packing_table_id,
            ),
          };
        }
        return { ...row, [field]: value };
      }),
    );
  };

  const columns = [
    {
      title: "Packing Line",
      key: "line_name",
      render: () =>
        lineMappings.find((m) => m?.packing_line_id === form.getFieldValue("packing_line_id"))?.packing_line?.name ||
        "-",
    },
    {
      title: "Packing Table",
      key: "packing_table_id",
      render: (_, record) => (
        <Select
          showSearch
          placeholder="Select Packing Table"
          value={record?.packing_table_id ? String(record?.packing_table_id) : undefined}
          style={{ width: "100%" }}
          optionFilterProp="children"
          onChange={(value) => updateRowField(record.key, "packing_table_id", value)}
        >
          {packingTableOptions.map((item) => (
            <Select.Option key={item.value} value={item.value}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Packer Man",
      key: "operator_id",
      render: (_, record) => <Input value={record?.operator_name || "N/A"} disabled />,
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      render: (_, record) => (
        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => deleteRow(record.key)} />
      ),
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ plan_date: moment() }}>
          <Row gutter={16}>
            <Col flex="1 1 20%">
              <Form.Item
                name="plan_date"
                label="Plan Date"
                rules={[{ required: true, message: "Plan Date is required" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col flex="2 1 20%">
              <Form.Item name="po_map_id" label="PO" rules={[{ required: true, message: "PO is required" }]}>
                <Select
                  showSearch
                  filterOption={false}
                  placeholder="Type to search PO"
                  onSearch={(value) => {
                    clearTimeout(poSearchTimer.current);
                    poSearchTimer.current = setTimeout(() => fetchPos(value), 400);
                  }}
                  onChange={(poMapId) => {
                    const poRecord = poOptions.find((item) => item?.po_map_id === poMapId);
                    setPoDetailsToForm(poRecord);
                  }}
                  options={poOptions.map((item) => ({
                    value: item?.po_map_id,
                    label: item?.po_maps?.original_po || "-",
                  }))}
                />
              </Form.Item>
            </Col>
            <Col flex="1 1 20%">
              <Form.Item label="Sales Contract" name="sales_contract_name">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col flex="1 1 20%">
              <Form.Item label="Buyer" name="buyer_name">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col flex="1 1 20%">
              <Form.Item
                label={selectedPo?.buying_office?.name ? "Buying Office" : "Factory"}
                name="buying_office_or_factory_name"
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="packing_line_id"
                label="Packing Line"
                rules={[{ required: true, message: "Packing Line is required" }]}
              >
                <Select
                  placeholder="Select Packing Line"
                  onChange={(lineId) => buildRowsFromLine(lineId)}
                  options={[
                    ...new Map(
                      lineMappings.map((item) => [
                        item.packing_line_id,
                        { value: item.packing_line_id, label: item?.packing_line?.name || "-" },
                      ]),
                    ).values(),
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row style={{ marginBottom: 12 }}>
            <Col span={24} style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addRow}
                disabled={!selectedPackingLineId}
              >
                Add Row
              </Button>
            </Col>
          </Row>

          <Table rowKey="key" loading={loading} pagination={false} dataSource={rows} columns={columns} />
        </Form>
      </Card>
    </div>
  );
});

PackingAssignmentForm.displayName = "PackingAssignmentForm";
export default PackingAssignmentForm;
